import React, { Fragment, useEffect } from "react";
import { Seo, Breadcrumb, ScrollTopBtn } from "../components";
import { HeaderThree, FooterTwo } from "../containers";

export default function TermsAndConditions() {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Fragment>
      <Seo
        title="Terms & Conditions - Tech Ayukta"
        description="Review the Terms & Conditions of Tech Ayukta to understand our user policies, service limitations, legal obligations, and DPDP compliance."
        keywords="Tech Ayukta Terms, Terms and Conditions, User Agreement, Service Policy, Legal Disclaimer, DPDP 2025, Data Protection"
        canonical="https://www.techayukta.com/terms-and-conditions"
      />

      <HeaderThree />
      <Breadcrumb
        pageName="Terms & Conditions"
        bgImage="/images/breadcumb/breadcumb-bg.jpg"
      />

      <div className="container mx-auto px-4 px-md-5 py-5 py-md-6">
        <h1 className="text-3xl fw-bold mb-4 text-center text-md-start">
          Terms & Conditions
        </h1>

        <p className="mb-4">
          Welcome to <strong>Tech Ayukta</strong>. By accessing, browsing, or using our website and services, you agree to comply with and be bound by the following Terms & Conditions. If you do not agree with these terms, please refrain from using our website or services.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">1. Acceptance of Terms</h4>
        <p className="mb-4">
          These Terms constitute a legally binding agreement between you and <strong>Tech Ayukta</strong>. Continued use of our website implies your acceptance of all current and future updates to these terms. It’s your responsibility to check this page regularly for updates.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">2. Use of Services</h4>
        <p className="mb-4">
          You agree to use our services solely for lawful purposes. You must not use the website to damage, disable, or otherwise harm any part of our platform or network security. Unauthorized access attempts and other violations are strictly prohibited.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">3. Intellectual Property</h4>
        <p className="mb-4">
          All website content—including text, graphics, logos, icons, images, and software—is the exclusive property of <strong>Tech Ayukta</strong> and protected by copyright and trademark laws. Material may not be used, copied, or distributed without prior written consent.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">4. Course Enrollment & Payments</h4>
        <p className="mb-4">
          When enrolling in our courses or training programs, you agree to provide accurate and complete information. Payment methods, service costs, and refund timelines are clearly disclosed during checkout. Payments made are non-transferable and non-refundable unless stated in our Refund Policy. Real-time status updates for your order or enrollment are provided via your account dashboard or email notifications.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">5. Limitation of Liability</h4>
        <p className="mb-4">
          While we strive for accuracy and reliability, <strong>Tech Ayukta</strong> does not guarantee uninterrupted or error-free access to its website or training services. We are not liable for any direct, indirect, or incidental damages arising from your use or inability to use our website, products, or services.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">6. User Conduct</h4>
        <p className="mb-4">
          Users agree to maintain respectful communication during training or when interacting with instructors and other participants. Any misuse, harassment, or violation of these or ethical standards may result in termination of access without refund.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">7. Third-Party Links</h4>
        <p className="mb-4">
          Our website may contain links to third-party websites for your convenience. These links do not imply endorsement, and <strong>Tech Ayukta</strong> is not responsible for content, accuracy, or data protection practices of these external sites.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">8. Privacy, Consent & Data Usage (DPDP Compliance)</h4>
        <p className="mb-4">
          We collect and process personal information as outlined in our <a href="/privacy-policy" className="text-decoration-none text-primary">Privacy Policy</a>. By using our services, you consent to our data collection, processing, and communication practices in accordance with India’s Digital Personal Data Protection Act (DPDP, 2023) and applicable rules. You have the right to access, correct, withdraw, or delete your personal data at any time. To manage your data preferences or file a privacy complaint, please email our Data Protection Officer at <a href="mailto:techsupport@techayukta.com" className="text-decoration-none text-primary">techsupport@techayukta.com</a>.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">9. Accessibility Statement</h4>
        <p className="mb-4">
          Tech Ayukta aims to comply with applicable accessibility laws in India. If you have difficulty accessing content, please contact us so we can provide appropriate assistance.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">10. Dispute Resolution</h4>
        <p className="mb-4">
          In the event of any dispute, parties agree to first attempt resolution by mediation. If mediation fails, disputes will be subject to arbitration as per Indian Arbitration and Conciliation Act, 1996. Jurisdiction will remain in Uttar Pradesh, India.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">11. Termination of Access</h4>
        <p className="mb-4">
          We reserve the right to suspend or terminate user access at any time for violations of these Terms & Conditions or conduct deemed harmful to our platform or users.
        </p>

        <h4 className="h5 fw-semibold mt-5 mb-2">12. Updates & Notification of Changes</h4>
        <p className="mb-4">
          <strong>Tech Ayukta</strong> reserves the right to revise these Terms & Conditions at any time. Major changes will be notified by email or site banner. Continued use of the platform after changes take effect indicates your acceptance of the revised Terms.
        </p>

        <p className="mt-5 text-sm">
          For questions or concerns about these Terms & Conditions, data protection, privacy, payments, or accessibility, contact us at{" "}
          <a
            href="mailto:techsupport@techayukta.com"
            className="text-decoration-none text-primary"
          >
            techsupport@techayukta.com
          </a>.
        </p>
      </div>

      <FooterTwo />
      <ScrollTopBtn />
    </Fragment>
  );
}
